import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ChevronLeft, MessageCircle, Play, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import {
  useCart,
  singleWhatsappUrl,
  whatsappConfigured,
} from "@/context/CartContext";
import {
  products,
  getProduct,
  sizes,
  defaultSizeId,
  getSize,
  formatPrice,
} from "@/config/products";
import Navbar from "@/components/sections/Navbar";
import AnnouncementBar from "@/components/sections/AnnouncementBar";
import Footer from "@/components/sections/Footer";
import CartDrawer from "@/components/sections/CartDrawer";
import FloatingWhatsApp from "@/components/sections/FloatingWhatsApp";
import MagneticButton from "@/components/MagneticButton";

/**
 * ProductPage — plantilla única y reutilizable para /products/:slug.
 * Reusa i18n existente (t.products[key], t.flagship.*, t.whatsapp.*),
 * el carrito, el helper de WhatsApp, y estilos ya definidos.
 *
 * Shared-element: el image container tiene `layoutId={product-image-${id}}`
 * para que framer-motion anime la transición desde la Collection card.
 */
export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const { t } = useLanguage();
  const { addItem, setOpen: setCartOpen } = useCart();
  const reduce = useReducedMotion();
  const [sizeId, setSizeId] = useState(defaultSizeId);
  const [status, setStatus] = useState("idle"); // idle | adding | added
  const [imgIdx, setImgIdx] = useState(0); // int | "video"
  const [errMsg, setErrMsg] = useState("");
  const [needSize, setNeedSize] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const waOn = whatsappConfigured();

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (imgIdx === "video") {
      try { v.currentTime = 0; v.play(); } catch {}
    } else {
      try { v.pause(); v.currentTime = 0; } catch {}
    }
  }, [imgIdx]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "instant" });
  }, [slug, reduce]);

  if (!product) {
    return (
      <>
        <AnnouncementBar />
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-6 text-center">
          <p className="eyebrow text-[10px] text-[#999]">404</p>
          <h1 className="mt-4 font-serif-display text-3xl text-[#111]">
            {t.product?.notFound || "Product not found"}
          </h1>
          <Link
            to="/"
            className="tactile-btn mt-8 rounded-full border border-[#111] px-7 py-3 font-body text-[13px] text-[#111]"
          >
            {t.product?.backHome || "Back to shop"}
          </Link>
        </main>
      </>
    );
  }

  const size = getSize(sizeId);
  const price = product.pricedBySize ? size.price : product.basePrice;
  const name = t.products[product.key].name;
  const added = status === "added";

  const handleAdd = ({ openDrawerAfter = false } = {}) => {
    if (status !== "idle") return;
    // Validación: si el producto se cotiza por talla y no hay talla, resalta
    if (product.pricedBySize && !sizeId) {
      setNeedSize(true);
      document.querySelector("[data-testid^=product-size-]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setNeedSize(false), 1600);
      return;
    }
    setStatus("adding");
    setErrMsg("");
    try {
      addItem(product, size.label, price, 1);
      if (navigator.vibrate) navigator.vibrate(8);
      setStatus("added");
      if (openDrawerAfter) {
        // Abrimos el drawer justo después de la confirmación (evita apertura prematura)
        window.setTimeout(() => {
          setCartOpen(true);
          window.dispatchEvent(new CustomEvent("kelo:cart-highlight", { detail: { productId: product.id, size: size.label } }));
        }, 240);
      }
      window.setTimeout(() => setStatus("idle"), 1600);
    } catch (err) {
      console.error("ProductPage addItem failed", err);
      setStatus("idle");
      setErrMsg(t.product?.addError || "Unable to add this item. Please try again.");
    }
  };

  const order = () =>
    window.open(
      singleWhatsappUrl(
        { productKey: product.key, size: size.label, qty: 1, price },
        t
      ),
      "_blank"
    );

  const enterY = reduce ? 0 : 14;
  const enterDur = reduce ? 0 : 0.55;
  const stagger = (i) => ({
    initial: { opacity: 0, y: enterY },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: enterDur,
      delay: reduce ? 0 : 0.42 + i * 0.09,
      ease: [0.22, 1, 0.36, 1],
    },
  });

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main data-testid="product-page" className="bg-white">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="mx-auto max-w-7xl px-5 pt-6 lg:px-10 lg:pt-10"
        >
          <ol className="flex items-center gap-2 font-body text-[11px] text-[#8a8a8a]">
            <li>
              <Link to="/" className="hover:text-[#111]">
                {t.product?.breadcrumbHome || "Home"}
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/#collection" className="hover:text-[#111]">
                {t.nav.bestSellers}
              </Link>
            </li>
            <li>/</li>
            <li className="text-[#111]">{name}</li>
          </ol>
        </motion.nav>

        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-24 pt-6 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:pb-32 lg:pt-8">
          {/* Gallery */}
          <div>
            <motion.div
              layoutId={reduce ? undefined : `product-image-${product.id}`}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-2xl bg-[#f7f7f5]"
            >
              {imgIdx === "video" && product.video ? (
                <video
                  ref={videoRef}
                  data-testid="product-page-video"
                  src={product.video}
                  poster={product.videoPoster || product.images[0]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onError={() => { /* codec/network fail — fall back silently */ }}
                  className="aspect-square w-full object-cover"
                />
              ) : (
                <img
                  data-testid="product-page-image"
                  src={product.images[imgIdx] || product.images[0]}
                  alt={name}
                  className="aspect-square w-full object-cover"
                  fetchPriority="high"
                />
              )}
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/5" />
            </motion.div>
            {(product.images.length > 1 || product.video) && (
              <motion.div
                {...stagger(0)}
                className="mt-4 flex gap-3"
              >
                {product.images.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setImgIdx(i)}
                    aria-label={`View ${i + 1}`}
                    className={`h-16 w-16 overflow-hidden rounded-lg border transition-all ${
                      i === imgIdx
                        ? "border-[#111] shadow-[0_0_0_1px_#111_inset]"
                        : "border-[#e6e6e6] hover:border-[#999]"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
                {product.video && (
                  <button
                    data-testid="product-thumb-video"
                    onClick={() => setImgIdx("video")}
                    aria-label="Play product video"
                    className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border transition-all press ${
                      imgIdx === "video"
                        ? "border-[#111] shadow-[0_0_0_1px_#111_inset]"
                        : "border-[#e6e6e6] hover:border-[#999]"
                    }`}
                  >
                    <img
                      src={product.videoPoster || product.images[0]}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 bg-black/25" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/95 shadow-sm">
                        <Play className="h-3 w-3 translate-x-[1px] fill-[#111] text-[#111]" />
                      </span>
                    </span>
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 rounded bg-black/60 px-1 py-0.5 font-body text-[7px] font-semibold uppercase tracking-[0.14em] text-white">
                      Video
                    </span>
                  </button>
                )}
              </motion.div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col lg:pt-2">
            <motion.h1
              {...stagger(0)}
              className="font-serif-display text-3xl leading-tight text-[#111] sm:text-4xl lg:text-[44px] lg:leading-[1.1]"
            >
              {name}
            </motion.h1>

            <motion.p
              {...stagger(1)}
              className="mt-4 font-body text-[14px] font-light leading-relaxed text-[#666]"
            >
              {t.products[product.key].short}
            </motion.p>

            <motion.div {...stagger(2)} className="mt-6">
              <span
                data-testid="product-price"
                className="font-serif-display text-3xl text-[#111]"
              >
                {formatPrice(price)}
              </span>
              <span className="ml-2 font-body text-[11px] uppercase tracking-[0.18em] text-[#8a8a8a]">
                USD
              </span>
            </motion.div>

            {product.pricedBySize && (
              <motion.div {...stagger(3)} className="mt-8">
                <p className="eyebrow text-[10px] text-[#8a8a8a]">
                  {t.flagship.selectSize}
                </p>
                <div
                  role="radiogroup"
                  className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4"
                >
                  {sizes.map((s) => {
                    const selected = s.id === sizeId;
                    return (
                      <button
                        key={s.id}
                        data-testid={`product-size-${s.id}`}
                        role="radio"
                        aria-checked={selected}
                        data-selected={selected ? "1" : "0"}
                        onClick={() => setSizeId(s.id)}
                        className={`variant-chip press rounded-xl border px-3 py-2.5 text-left ${
                          selected
                            ? "border-[#111] bg-[#f5f5f3]"
                            : "border-[#e2e2e2] bg-white hover:border-[#9a9a9a]"
                        }`}
                      >
                        <span className="block font-body text-[13px] font-medium text-[#111]">
                          {s.label}
                        </span>
                        <span className="mt-0.5 block font-body text-[10px] text-[#999]">
                          {formatPrice(s.price)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            <motion.div
              {...stagger(4)}
              className="mt-8 flex flex-col gap-2 sm:flex-row"
            >
              <MagneticButton
                data-testid="product-add-cart"
                onClick={() => handleAdd()}
                disabled={status === "adding"}
                strength={7}
                radius={80}
                className="tactile-btn halo-luxe cta-breathe flex min-w-[190px] flex-1 items-center justify-center gap-2 rounded-full bg-[#111] px-7 py-3 font-body text-[13px] font-medium text-white hover:bg-[#2a2a2a] disabled:opacity-90"
              >
                {status === "added" ? (
                  <>
                    <Check className="h-4 w-4" /> {t.flagship.added}
                  </>
                ) : status === "adding" ? (
                  <>
                    <span className="loader-dot" aria-hidden="true" />
                    {t.product?.adding || "Adding…"}
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-4 w-4" /> {t.flagship.addToCart}
                  </>
                )}
              </MagneticButton>
              {waOn && (
                <MagneticButton
                  data-testid="product-whatsapp"
                  onClick={order}
                  strength={5}
                  radius={70}
                  className="tactile-btn flex flex-1 items-center justify-center gap-2 rounded-full border border-[#ddd] px-7 py-3 font-body text-[13px] text-[#111] hover:border-[#111]"
                >
                  <MessageCircle className="h-4 w-4" /> {t.flagship.buy}
                </MagneticButton>
              )}
            </motion.div>

            <motion.p
              {...stagger(5)}
              className="mt-5 font-body text-[11px] uppercase tracking-[0.18em] text-[#8a8a8a]"
            >
              {t.hero?.trustBadge}
            </motion.p>

            {/* Specs */}
            <motion.dl
              {...stagger(6)}
              className="mt-10 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-[#f0f0f0] pt-8 font-body text-[13px]"
            >
              {[
                ["stone", t.product?.stone || "Stone"],
                ["cut", t.product?.cut || "Cut"],
                ["metal", t.product?.metal || "Metal"],
                ["backing", t.product?.backing || "Backing"],
              ].map(([k, label]) => (
                <div key={k}>
                  <dt className="eyebrow text-[9px] text-[#999]">{label}</dt>
                  <dd className="mt-1 text-[#111]">
                    {t.product?.specs?.[product.key]?.[k] ||
                      t.product?.specs?.default?.[k] ||
                      "—"}
                  </dd>
                </div>
              ))}
            </motion.dl>

            {/* Included */}
            <motion.div
              {...stagger(7)}
              className="mt-8 rounded-2xl bg-[#f9f9f7] p-5"
            >
              <p className="eyebrow text-[10px] text-[#8a8a8a]">
                {t.product?.includes || "What's included"}
              </p>
              <ul className="mt-3 space-y-2 font-body text-[13px] text-[#333]">
                {(t.hero?.includes || []).map((line, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#111]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Related */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="border-t border-[#f0f0f0] bg-[#fafaf8] py-20"
        >
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <h2 className="mb-10 text-center font-serif-display text-2xl text-[#111] sm:text-3xl">
              {t.product?.related || "You may also like"}
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {products
                .filter((p) => p.id !== product.id)
                .map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    data-testid={`product-related-${p.id}`}
                    className="tactile-card group overflow-hidden rounded-2xl border border-[#eee] bg-white"
                  >
                    <div className="overflow-hidden bg-[#f7f7f5]">
                      <img
                        src={p.thumb}
                        alt={t.products[p.key].name}
                        className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif-display text-lg text-[#111]">
                        {t.products[p.key].name}
                      </h3>
                      <p className="mt-1 font-body text-[13px] text-[#8a8a8a]">
                        {formatPrice(p.basePrice)}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </motion.section>

        {/* Sticky mobile buy bar */}
        <motion.div
          initial={{ y: 96, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-30 border-t border-[#eee] bg-white/95 px-4 py-3 shadow-[0_-8px_24px_-16px_rgba(0,0,0,0.15)] backdrop-blur-lg lg:hidden"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
          data-testid="product-mobile-sticky"
        >
          <div className="flex items-center gap-2">
            <span className="mr-1 font-serif-display text-lg text-[#111]">
              {formatPrice(price)}
            </span>
            <button
              data-testid="product-mobile-add"
              onClick={() => handleAdd({ openDrawerAfter: true })}
              disabled={status === "adding"}
              className="halo-luxe cta-breathe press flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#111] px-4 py-2.5 font-body text-[13px] font-medium text-white disabled:opacity-90"
            >
              {status === "added" ? (
                <><Check className="h-4 w-4" /> {t.flagship.added}</>
              ) : status === "adding" ? (
                <><span className="loader-dot" aria-hidden="true" /> {t.product?.adding || "Adding…"}</>
              ) : (
                t.flagship.addToCart
              )}
            </button>
            {waOn && (
              <button
                data-testid="product-mobile-wa"
                onClick={order}
                aria-label="WhatsApp"
                className="press flex h-11 w-11 items-center justify-center rounded-full border border-[#111]"
              >
                <MessageCircle className="h-4 w-4 text-[#111]" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Back link (visible, top-left floating) */}
        <button
          data-testid="product-back"
          onClick={() => navigate(-1)}
          aria-label={t.product?.back || "Back"}
          className="fixed left-4 top-[76px] z-30 flex h-10 w-10 items-center justify-center rounded-full border border-[#e2e2e2] bg-white/90 text-[#111] shadow-sm backdrop-blur-md press hover:bg-[#111] hover:text-white lg:left-8"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </main>
      <Footer />
      <CartDrawer />
      <FloatingWhatsApp />
    </>
  );
}
